/**
 * sessionBootstrap.ts
 *
 * Single source of truth for hydrating `useSession` from Supabase Auth.
 *
 * Lifecycle:
 *   1. On app mount: read the persisted session (`supabase.auth.getSession()`).
 *   2. If signed in: resolve the user's single shop_members row + that
 *      shop's locations + the user's preferred current_location_id, push
 *      everything into the Zustand store, mark status = 'authenticated'.
 *   3. Subscribe to `auth.onAuthStateChange` to react to SIGNED_IN /
 *      SIGNED_OUT / TOKEN_REFRESHED events from elsewhere (other tabs,
 *      password-reset links, expired tokens).
 *
 * Account model (Epic 0.X locked):
 *   - A user account belongs to exactly ONE shop. The `shop_members` table
 *     has a UNIQUE(user_id) constraint enforcing this.
 *   - The header dropdown switches LOCATIONS within that shop, not shops.
 *
 * Three-shell note: kiosk routes never call `useAuthBootstrap` (kiosk is
 * anonymous-only — CLAUDE.md guardrail). Only the root <App/> for
 * desktop/mobile shells invokes it.
 */
import { useEffect } from 'react';
import { supabase } from './supabase';
import { useSession, type LocationKind, type LocationOption, type Role } from './store';

interface UserProfileRow {
  current_location_id: string | null;
  display_name: string | null;
}

interface MembershipRow {
  role: Role;
  shops: { id: string; name: string } | null;
}

interface LocationRow {
  id: string;
  name: string;
  kind: LocationKind;
  is_default: boolean;
  archived: boolean;
}

/**
 * Pull the user's profile + single membership + that shop's locations from
 * Supabase and push them into the store. Pure async fn — no React.
 */
export async function hydrateSession(userId: string, email: string): Promise<void> {
  // Profile + membership are independent → fetch in parallel. Locations
  // depend on the shop_id resolved by the membership query, so it runs
  // after.
  const [{ data: profile }, { data: membership }] = await Promise.all([
    supabase
      .from('users')
      .select('current_location_id, display_name')
      .eq('id', userId)
      .maybeSingle<UserProfileRow>(),
    supabase
      .from('shop_members')
      .select('role, shops!shop_members_shop_id_fkey!inner(id, name)')
      .eq('user_id', userId)
      .is('disabled_at', null)
      .maybeSingle<MembershipRow>(),
  ]);

  // Ensure a public.users profile row exists — no trigger creates it automatically.
  // The upsert is a no-op if the row already exists (onConflict id).
  if (!profile) {
    await supabase
      .from('users')
      .upsert({ id: userId, email }, { onConflict: 'id', ignoreDuplicates: true });
  }

  if (!membership?.shops) {
    // Signed in but no active membership — render the app as authenticated
    // with no shop context. The SubscriptionGate / role checks will render
    // their respective "no shop" surfaces.
    useSession.setState({
      status: 'authenticated',
      userId,
      email,
      displayName: profile?.display_name ?? null,
      currentShopId: null,
      shopName: null,
      role: null,
      availableLocations: [],
      currentLocationId: null,
    });
    return;
  }

  const shopId = membership.shops.id;
  const shopName = membership.shops.name;
  const role = membership.role;

  // Fetch all non-archived locations + this user's location assignments in
  // parallel. Assignments are only checked for non-owners; owners always see
  // every location regardless of any assignment rows.
  const [{ data: locationsData }, { data: assignmentData }] = await Promise.all([
    supabase
      .from('locations')
      .select('id, name, kind, is_default, archived')
      .eq('shop_id', shopId)
      .eq('archived', false)
      .order('is_default', { ascending: false })
      .order('name', { ascending: true })
      .returns<LocationRow[]>(),
    supabase
      .from('shop_member_locations')
      .select('location_id')
      .eq('user_id', userId)
      .eq('shop_id', shopId),
  ]);

  const allLocations: LocationOption[] = (locationsData ?? []).map((l) => ({
    id: l.id,
    name: l.name,
    kind: l.kind,
    is_default: l.is_default,
  }));

  // Owner always gets all locations. For manager/staff, filter to assigned set
  // if any assignments exist; empty set means no restriction.
  const assignedIds = (assignmentData ?? []).map((a) => a.location_id as string);
  const availableLocations =
    role === 'owner' || assignedIds.length === 0
      ? allLocations
      : allLocations.filter((l) => assignedIds.includes(l.id));

  // Pick the active location:
  //   1. The user's stored preference if it is still in the available set.
  //   2. The shop's default location (is_default=true).
  //   3. The first available location (always exists — the shops trigger
  //      seeds one on insert).
  //   4. null (only if locations table is somehow empty).
  const preferred = profile?.current_location_id ?? null;
  const currentLocationId =
    preferred && availableLocations.some((l) => l.id === preferred)
      ? preferred
      : (availableLocations.find((l) => l.is_default)?.id ?? availableLocations[0]?.id ?? null);

  useSession.setState({
    status: 'authenticated',
    userId,
    email,
    displayName: profile?.display_name ?? null,
    currentShopId: shopId,
    shopName,
    role,
    availableLocations,
    currentLocationId,
  });
}

/**
 * React-side bootstrap: call once at the top of any shell that requires auth.
 * Idempotent — subsequent renders re-attach the same listener-once via the
 * effect's cleanup.
 */
export function useAuthBootstrap(): void {
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = data.session;
      if (!session?.user) {
        useSession.getState().clear();
        return;
      }
      await hydrateSession(session.user.id, session.user.email ?? '');
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT' || !session?.user) {
        useSession.getState().clear();
        return;
      }
      void hydrateSession(session.user.id, session.user.email ?? '');
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);
}
